import { useMemo, useState } from "react";
import { TextField } from "../../common/inputFiled";
import toast from "react-hot-toast";

const MARITAL_STATUS_OPTIONS = [
  { value: "Single", label: "Single" },
  { value: "Married", label: "Married" },
  { value: "Divorced", label: "Divorced" },
  { value: "Widowed", label: "Widowed" },
  { value: "Separated", label: "Separated" },
];

const isEmail = (val) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(val || "").trim());

const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function AppSetupPersonalInfoStep({ signupDraft, onSubmit, loading }) {
  const [sameAsAboveEmail, setSameAsAboveEmail] = useState(true);
  const [sameAsPersonalWorkMobile, setSameAsPersonalWorkMobile] = useState(true);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(() => ({
    first_name: signupDraft?.firstName || "",
    last_name: signupDraft?.lastName || "",
    full_name: [signupDraft?.firstName, signupDraft?.lastName].filter(Boolean).join(" "),
    personal_email_id: signupDraft?.email || "",
    work_email_id: signupDraft?.email || "",
    personal_mobile: signupDraft?.phoneNumber || "",
    work_mobile: signupDraft?.phoneNumber || "",
    country_code_personal: signupDraft?.countryCode || "+91",
    work_mobile_country_code: signupDraft?.countryCode || "+91",
    country_code_home: "+91",
    country_code_home_fixed: "+91",
    country_code_work_fixed: "+91",
    secondary_email: "",
    secondary_mobile: "",
    home_fixed: "",
    work_fixed: "",
    marital_status: "",
    date_of_birth: "",
    date_of_anniversary: "",
    birth_city_country: "",
    current_city_region_country: "",
    basic_pincode_home: "",
    personal_mobile_valid: true,
    work_mobile_valid: true,
  }));

  const summaryItems = useMemo(
    () => [
      { label: "Name", value: formData.full_name },
      { label: "Email", value: formData.personal_email_id },
      {
        label: "Mobile",
        value: `${formData.country_code_personal || ""} ${formData.personal_mobile || ""}`.trim(),
      },
    ],
    [formData]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (sameAsAboveEmail && name === "personal_email_id") {
        next.work_email_id = value;
      }
      return next;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(formData.work_email_id || "").trim()) nextErrors.work_email_id = "Enter Work Email";
    if (!sameAsPersonalWorkMobile && !String(formData.work_mobile || "").trim()) {
      nextErrors.work_mobile = "Enter Work Mobile";
    }
    if (!String(formData.marital_status || "").trim()) nextErrors.marital_status = "Select Marital Status";
    if (!String(formData.date_of_birth || "").trim()) nextErrors.date_of_birth = "Enter Date of Birth";
    if (formData.marital_status === "Married" && !String(formData.date_of_anniversary || "").trim()) {
      nextErrors.date_of_anniversary = "Enter Anniversary Date";
    }
    if (formData.work_email_id && !isEmail(formData.work_email_id)) {
      nextErrors.work_email_id = "Enter a valid email";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    const payload = {
      ...formData,
      work_email_id: sameAsAboveEmail ? formData.personal_email_id : formData.work_email_id,
      work_mobile: sameAsPersonalWorkMobile ? formData.personal_mobile : formData.work_mobile,
      work_mobile_country_code: sameAsPersonalWorkMobile
        ? formData.country_code_personal
        : formData.work_mobile_country_code,
      work_mobile_valid: true,
    };

    onSubmit(payload);
  };

  return (
    <div className="mobile-scroll-y h-[var(--app-height,100dvh)] bg-[#F3F0FF] px-4 py-6 sm:py-8">
      <div className="mx-auto w-full max-w-[520px] p-4 sm:p-8">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-semibold text-gray-900">Personal Information</h2>
          <p className="mt-2 text-sm text-gray-600">
            Complete your basic profile details. Fields marked with * are required.
          </p>
        </div>

        <div className="mb-5 rounded-2xl border border-violet-100 bg-white p-4 shadow-sm">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#643ECF]">From signup</p>
          <div className="space-y-2">
            {summaryItems.map((item) => (
              <div key={item.label} className="flex justify-between gap-3 text-sm">
                <span className="text-gray-500">{item.label}</span>
                <span className="font-medium text-gray-900 text-right break-all">{item.value || "—"}</span>
              </div>
            ))}
          </div>
        </div>

        <form className="space-y-4 rounded-3xl border border-violet-100 bg-white p-6 shadow-xl shadow-violet-200/30" onSubmit={handleSubmit} noValidate>
          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <label className="text-xs input-lable">Work Email Id<span className="text-red-500">*</span></label>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-500">Same as above</span>
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={sameAsAboveEmail}
                  onChange={() => {
                    setSameAsAboveEmail((prev) => {
                      const next = !prev;
                      if (next) {
                        setFormData((f) => ({ ...f, work_email_id: f.personal_email_id }));
                      }
                      return next;
                    });
                  }}
                />
                <div className="relative inline-flex h-5 w-9 items-center rounded-full bg-gray-200 peer-checked:bg-[#6938EF] after:absolute after:left-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-[16px]" />
              </label>
            </div>
            <TextField
              name="work_email_id"
              value={formData.work_email_id}
              onChange={handleChange}
              disabled={sameAsAboveEmail}
              startIcon="./input-logos/sms.svg"
              placeholder="Enter Work Email"
              error={errors.work_email_id}
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <label className="text-xs input-lable">Work Mobile<span className="text-red-500">*</span></label>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-500">Same as Mobile Number</span>
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={sameAsPersonalWorkMobile}
                  onChange={() => setSameAsPersonalWorkMobile((prev) => !prev)}
                />
                <div className="relative inline-flex h-5 w-9 items-center rounded-full bg-gray-200 peer-checked:bg-[#6938EF] after:absolute after:left-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-[16px]" />
              </label>
            </div>
            {!sameAsPersonalWorkMobile && (
              <TextField
                name="work_mobile"
                value={formData.work_mobile}
                onChange={handleChange}
                startIcon="./input-logos/call.svg"
                placeholder="Enter Work Mobile"
                error={errors.work_mobile}
              />
            )}
          </div>

          <TextField
            label="Marital Status"
            name="marital_status"
            required
            type="select"
            value={formData.marital_status}
            onChange={handleChange}
            options={MARITAL_STATUS_OPTIONS}
            startIcon="./input-logos/ring.svg"
            error={errors.marital_status}
          />

          <TextField
            label="Date of Birth"
            name="date_of_birth"
            required
            type="date"
            value={formData.date_of_birth}
            onChange={handleChange}
            max={getTodayISO()}
            startIcon="./input-logos/calander.svg"
            error={errors.date_of_birth}
          />

          {formData.marital_status === "Married" && (
            <TextField
              label="Anniversary Date"
              name="date_of_anniversary"
              required
              type="date"
              value={formData.date_of_anniversary}
              onChange={handleChange}
              max={getTodayISO()}
              startIcon="./input-logos/calander.svg"
              error={errors.date_of_anniversary}
            />
          )}

          <button
            type="submit"
            disabled={loading}
            className="submit-button w-full rounded-xl py-3.5 text-sm font-semibold shadow-lg shadow-violet-500/25 disabled:opacity-60"
          >
            {loading ? "Saving..." : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
